import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AuthContext from './AuthContext';
import AgentSelectionModal from './AgentSelectionModal';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [isProcessing, setIsProcessing] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [showAgentSelection, setShowAgentSelection] = useState(false);

  const getBackendUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  };

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (sessionId) {
      // Poll payment status
      pollPaymentStatus(sessionId);
    } else {
      // No session ID, redirect to home
      navigate('/');
    }
  }, [searchParams, navigate]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setIsProcessing(false);
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${getBackendUrl()}/api/checkout/status/${sessionId}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        setIsProcessing(false);
        
        // Fetch updated subscription details
        fetchCurrentSubscription();
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setIsProcessing(false);
        return;
      }

      // If payment is still pending, continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
      setIsProcessing(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/subscription/current`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscription(data.subscription);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleContinue = () => {
    // Navigator and Advocate plans need agent selection
    // Champion gets all 8 agents automatically, Explorer gets 1 agent automatically
    if (subscription && 
        (subscription.subscription_name === 'Navigator' ||
         subscription.subscription_name === 'Advocate')) {
      // Show agent selection modal
      setShowAgentSelection(true);
    } else {
      // Explorer and Champion plans - go to dashboard
      navigate('/?onboarding_complete=true');
    }
  };

  const handleAgentSelectionComplete = () => {
    // After agent selection is complete, go to dashboard
    navigate('/?onboarding_complete=true');
  };

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-400 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-white mb-4">Processing Payment...</h2>
          <p className="text-gray-300">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="max-w-md w-full mx-auto p-8 bg-gradient-to-b from-zinc-900 to-black rounded-xl border border-green-400">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Payment Successful!</h2>
            <p className="text-gray-300 mb-6">
              Thank you for your subscription. Your payment has been processed successfully.
            </p>
            
            {subscription && (
              <div className="bg-gray-800 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">Subscription Details</h3>
                <p className="text-green-400 font-medium">{subscription.subscription_name} Plan</p>
                <p className="text-gray-300 text-sm">Status: {subscription.status}</p>
                {subscription.current_period_end && (
                  <p className="text-gray-300 text-sm">
                    Next billing: {new Date(subscription.current_period_end).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}
            
            <button
              onClick={handleContinue}
              className="w-full bg-green-400 hover:bg-green-500 text-black font-bold py-3 px-6 rounded-lg transition duration-300"
            >
              {subscription && (subscription.subscription_name === 'Navigator' || subscription.subscription_name === 'Advocate') 
                ? 'Continue to Agent Selection' 
                : 'Continue to Dashboard'}
            </button>
          </div>
        </div>

        {/* Agent Selection Modal */}
        <AgentSelectionModal
          isOpen={showAgentSelection}
          onClose={() => setShowAgentSelection(false)}
          onComplete={handleAgentSelectionComplete}
        />
      </div>
    );
  }

  if (paymentStatus === 'expired') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="max-w-md w-full mx-auto p-8 bg-gradient-to-b from-zinc-900 to-black rounded-xl border border-yellow-400">
          <div className="text-center">
            <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.982 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Payment Session Expired</h2>
            <p className="text-gray-300 mb-6">
              Your payment session has expired. Please try again.
            </p>
            <button
              onClick={handleContinue}
              className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-3 px-6 rounded-lg transition duration-300"
            >
              Return to Pricing
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="max-w-md w-full mx-auto p-8 bg-gradient-to-b from-zinc-900 to-black rounded-xl border border-red-400">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-400 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Payment Error</h2>
          <p className="text-gray-300 mb-6">
            There was an error processing your payment. Please try again or contact support.
          </p>
          <button
            onClick={handleContinue}
            className="w-full bg-red-400 hover:bg-red-500 text-black font-bold py-3 px-6 rounded-lg transition duration-300"
          >
            Return to Pricing
          </button>
        </div>
      </div>

      {/* Agent Selection Modal */}
      <AgentSelectionModal
        isOpen={showAgentSelection}
        onClose={() => setShowAgentSelection(false)}
        onComplete={handleAgentSelectionComplete}
      />
    </div>
  );
};

export default SubscriptionSuccess;